<template>
  <div class="certifications">
    <div class="certifications__container">
      <img
        v-if="externalCertifications.length"
        class="certifications__container__external-certification-logo"
        :src="externalCertifications[0].logo_image_url"
      />
      <div class="certifications__container__header">
        <h3 class="certifications__container__header__title">
          {{ attemptStatusTitle }}
        </h3>

        <p
          v-if="!externalCertifications.length"
          class="certifications__container__header__attempt-status-subtitle"
        >
          {{ attemptStatusSubTitle }}
        </p>
        <div
          v-else
          class="certifications__container__header__external-certification"
        >
          <p
            class="certifications__container__header__external-certification__subtitle"
          >
            {{ externalCertificationSubtitle }}
          </p>
          <button
            class="certifications__container__header__external-certification__link btn btn--primary"
            @click="openExternalCertificationUrl"
          >
            Get the {{ externalCertifications[0].short_name }}
            <ExternalLinkIcon />
          </button>
        </div>
      </div>
      <ul
        v-if="!externalCertifications.length"
        class="certifications__container__certifications-list"
      >
        <li
          v-for="certification in internalCertifications"
          :key="certification.id"
        >
          <Certification
            :learningPath="learningPath"
            :certification="certification"
            :lab="getCertificationLab(certification)"
          />
        </li>
      </ul>
    </div>
  </div>
</template>
<script>
import { format } from 'date-fns-tz'
import CONSTANTS from '@/constants'
import {
  hasPassedAttempt,
  lastAttempt as certLastAttempt,
  lastActiveVoucherFromCert,
  attemptRemainingHours,
} from '@/helpers'
import { ExternalLinkIcon } from 'vue-feather-icons'
import Certification from '@/components/certifications/Certification.vue'
import { fetchLabs } from '../../../models/content'
import { mapActions, mapState } from 'vuex'
import { closestDate } from '@/helpers/dates'

export default {
  name: 'CertificationsSection',
  components: {
    Certification,
    ExternalLinkIcon,
  },
  props: {
    learningPath: {
      type: Object,
      required: true,
    },
    certifications: {
      type: Array,
      required: true,
    },
  },
  data: () => {
    return {
      lastAttempt: null,
      labs: [],
    }
  },
  computed: {
    ...mapState({
      isAuthenticated: state => state.auth.isAuthenticated,
    }),
    internalCertifications() {
      return this.certifications?.filter(c => !c.is_external)
    },
    externalCertifications() {
      return this.certifications?.filter(c => c.is_external)
    },
    attemptStatusTitle() {
      if (this.everythingIsPassed && !this.externalCertifications.length) {
        return `Congratulations! You've earned the Certification${
          this.certifications?.length > 1 ? 's' : ''
        } associated with this Learning Path!`
      }
      return 'Ready to demonstrate your expertise?'
    },
    externalCertificationSubtitle() {
      const cert = this.externalCertifications[0]
      return `Get the ${cert.short_name} (${cert.name}) certification exam voucher.`
    },
    attemptStatusSubTitle() {
      let lastAttempt = null
      // many certs not passed
      if (this.internalCertifications?.length > 1 && !this.everythingIsPassed)
        return `This Learning Path has ${this.internalCertifications?.length} certifications available to validate your technical knowledge.`

      if (this.everythingIsPassed) return ''

      if (this.internalCertifications?.length === 1) {
        const cert = this.internalCertifications[0]
        const lastActiveVoucher = lastActiveVoucherFromCert(cert)
        if (lastActiveVoucher) {
          lastAttempt = certLastAttempt(cert)
          if (
            lastAttempt &&
            lastAttempt.status ===
              CONSTANTS.CERTIFICATIONS.ATTEMPT_STATUS.IN_PROGRESS
          ) {
            return `This exam is in progress. You have ${attemptRemainingHours(
              lastAttempt
            )} remaining.`
          }
          if (
            lastAttempt &&
            [
              CONSTANTS.CERTIFICATIONS.ATTEMPT_STATUS.FINISHED,
              CONSTANTS.CERTIFICATIONS.ATTEMPT_STATUS.TIMEDOUT,
            ].includes(lastAttempt.status) &&
            !hasPassedAttempt(lastAttempt)
          ) {
            return `You have ${
              lastActiveVoucher.remaining_attempts
            } retakes    ${this.retakeExpirationShortDate(lastActiveVoucher)}`
          }
          if (
            lastAttempt &&
            lastAttempt.status ===
              CONSTANTS.CERTIFICATIONS.ATTEMPT_STATUS.REVIEW
          ) {
            return `We're reviewing your exam. You'll get a notification when the results are in.`
          }
          return ''
        }
        return 'You have no available vouchers.'
      }
      return ''
    },
    everythingIsPassed() {
      const { internalCertifications } = this

      if (internalCertifications.length) {
        return internalCertifications.every(cert =>
          hasPassedAttempt(
            cert.user_status?.inactive_vouchers?.[0]?.inactive_attempts?.[0]
          )
        )
      }

      return false
    },
  },
  async created() {
    if (this.isAuthenticated) {
      try {
        const { internalCertifications } = this
        const labIds = internalCertifications
          .map(
            ({ versions }) =>
              versions[0]?.practice_lab || versions[0]?.questions_lab
          )
          .filter(Boolean)
        if (labIds?.length) {
          this.labs = await fetchLabs({ labIds })
          this.$store.dispatch('labs/getRunningLab')
        }
      } catch ({ message }) {
        this.setError(message)
      }
    }
  },
  mounted() {
    const { internalCertifications } = this
    if (internalCertifications?.length === 1) this.saveLastAttempt()
  },
  methods: {
    ...mapActions({
      setError: 'app/setError',
    }),
    getCertificationLab(certificate) {
      const version = certificate.versions?.[0]

      if (!version) return null

      const { practice_lab, questions_lab } = version
      const labId = practice_lab || questions_lab

      return labId ? this.labs.find(lab => lab.id === labId) : null
    },
    openExternalCertificationUrl() {
      window.location.href = this.externalCertifications[0].external_url
    },
    saveLastAttempt() {
      const cert = this.internalCertifications[0]
      this.lastAttempt = certLastAttempt(cert)
    },
    retakeExpirationShortDate(voucher) {
      return format(
        new Date(
          closestDate(
            voucher.next_attempt_expiration_date,
            voucher.expiration_date
          )
        ),
        'LLL d, yyyy'
      )
    },
  },
}
</script>
<style lang="scss" scoped>
.certifications {
  display: flex;
  justify-content: center;
  color: var(--white);
  padding: 60px 20px;

  @include desktop {
    padding: 60px 100px;
  }

  &__container {
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 1400px;
    width: 100%;

    &__external-certification-logo {
      width: 205px;

      @include desktop {
        width: 230px;
      }
    }
    &__header {
      text-align: center;
      &__title {
        font-family: var(--font-bold);
        font-size: rem(20px);
        line-height: rem(26px);
        margin: 0;

        @include desktop {
          font-size: rem(32px);
          line-height: rem(38px);
        }
      }
      &__attempt-status-subtitle {
        color: var(--cornflower-blue);
        font-family: var(--font-book);
        font-size: rem(16px);
        line-height: rem(24px);
        margin: 25px 0 0 0;
      }

      &__external-certification {
        &__subtitle {
          font-family: var(--font-book);
          font-size: rem(16px);
          line-height: rem(24px);
          margin: 10px 0 0 0;

          @include mobile {
            font-family: var(--font-medium);
            font-size: rem(18px);
            line-height: rem(24px);
          }
        }
        &__link {
          width: 100%;
          height: 36px;
          margin-top: 40px;
          font-family: var(--font-book);
          font-size: rem(14px);
          line-height: rem(20px);

          @include mobile {
            font-family: var(--font-medium);
            font-size: rem(16px);
            line-height: rem(23px);
            height: 48px;
            width: auto;
          }

          svg {
            height: 16px;
            width: 16px;
            fill: #fff !important;

            @include mobile {
              height: 24px;
              width: 24px;
            }
          }
        }
      }
    }
    &__certifications-list {
      display: flex;
      flex-flow: column;
      margin: 40px 0 0 0;
      padding: 0;
      list-style-type: none;
      gap: 40px;
    }
  }
}
</style>
